/**
 * WPS Groove Entity Configuration
 * 
 * Central configuration file for WPS (Welding Procedure Specification) transactions.
 * Contains validation types, field definitions, dropdown options, and entity structures.
 * Used across WPS, WPQ, and PQR transaction components.
 */

import { todayDate } from "../../utility/common/helpers/TodayDate";
import { Status } from "../../utility/common/constants/CommonConstants";
import { createDropdown } from "../../utility/common/DropdownComponent/DropdownHelper";

export const Layer_No = createDropdown("Layer No.");
export const Position_of_groove = createDropdown("Position Of Groove");
export const Tungsten_size = createDropdown("Tungsten Size");
export const Tungsten_type = createDropdown("Tungsten Type");
export const Weld_Progression = createDropdown("Weld Progression");
export const Flux_type = createDropdown("Flux Type");
export const Alloy_flux = createDropdown("Alloy Flux");
export const Alloy_element = createDropdown("Alloy element");
export const Addition_deletion_filler = createDropdown("Addition/Deletion of filler");
export const Addition_deletion_flux = createDropdown("Addition/Deletion of Flux(GTAW)");
export const Addition_deletion_changes = createDropdown("Addition/Deletion or Changes > 10% in suppl. filler metal");
export const Wrap_insulation_material = createDropdown("Wrap In Insulation Material");
export const Peening = createDropdown("Peening");
export const Supplementary_mf_control = createDropdown("Supplementary MF Control");
export const Thermal_process = createDropdown("Use of Thermal Process");
export const Oscillation = createDropdown("Oscillation");
export const Closed_to_out_chamber = createDropdown("Closed to out Chamber");
export const WPSH_Tech_BackGougingMethod = createDropdown("Back Gouging Method");
export const WPSH_PWHeatTre_Type = createDropdown("Type (Post Weld Heat Treatment (QW-407))");
export const NDT_Method = createDropdown("NDT Method");
export const WPSH_Initial_interpass_cleaning = createDropdown("Initial/ Interpass Cleaning");
export const WPSH_Tech_SurfacePreparation = createDropdown("Surface Preparation");
export const RepairLocationMarkedBy = createDropdown("Repair Location Marked By");
export const RepairRemovedByAfterRepairRemoval = createDropdown("Repair Removed By");
export const RepairFound = createDropdown("Repair Found");

// ============================================================================
// ENTITY STRUCTURES AND INITIALIZATION OBJECTS
// ============================================================================


// USED - Used in General.jsx and WPSTubesheetGeneral.jsx for company name dropdown options
export const company_name = [
  { value: "Godrej & Boyce Mfg. Co. Ltd", name: "WPSH_CompanyName" },
];

// USED - Used in WPS forms for process dropdown options
export const Process = [{ value: "GTAW", name: "WPSD_Process" }];

// USED - Used in WPS tubesheet forms for joint type dropdown options
export const joint_type = [
  { value: "Tube to Tubesheet", name: "WPSH_JD_JointType" },
];



// USED - Used in WPS forms for process name dropdown options
export const process_name = [{ value: "GTAW", name: "WPSD_Process" }];

// USED - Used in WPS forms for welding process dropdown options
export const WeldingProcess = [
  { value: "NA", name: "Na" },
  { value: "No Value", name: "No Value" },
  { value: "SMAW", name: "SMAW" },
];

// ============================================================================
// DROPDOWN OPTIONS AND CONSTANTS
// ============================================================================



// USED - Used in WPS forms for retainer dropdown options
export const Retainer = [
  { value: "", name: "Select" },
  { value: "NA", name: "NA" },
  { value: "Not used", name: "Not used" },
  {
    value: "Use of non metallic retainers",
    name: "Use of non metallic retainers",
  },
  {
    value: "Use of non fusing metal retainers",
    name: "Use of non fusing metal retainers",
  },
];

// USED - Used in WPS forms for base heat treatment dropdown options
export const Base_HT = [
  { value: "", name: "Select" },
  { value: "NA", name: "NA" },
  { value: "NACT", name: "NACT" },
  { value: "QT", name: "QT" },
  { value: "NT", name: "NT" },
  { value: "N", name: "N" },
  { value: "NACT/NT/QT", name: "NACT/NT/QT" },
  { value: "Solution annealing", name: "Solution annealing" },
];



// USED - Used in WPSTubesheetProcedureSpec.jsx for insulation material dropdown options
export const WPSH_PWHeatTre_Wrap_insulation_material = [
  { value: "", name: "Select" },
  { value: "NA", name: "NA" },
  { value: "Yes", name: "Yes" },
  { value: "No", name: "No" },
];

// ============================================================================
// PAGE FIELD DEFINITIONS
// ============================================================================

// Page 1 fields for different WPS types
const PAGE1_GROOVE_FIELDS = [
  'WPSH_Project_No', 'WPSH_WPS_No', 'WPSH_WPS_Revi_No', 'WPSH_WPS_Date', 'WPS_QualifiedTo',
  'WPSH_CompanyName', 'WPSH_SupportingPQR', 'WPSH_RefDoc', 'WPSH_Scope', 'WPSH_JointDesc',
  'WPSH_BM_Type_Type', 'WPSH_BM_Type_GNo', 'WPSH_BM_Type_PNo', 'WPSH_BM_WeldingTo_Type',
  'WPSH_BM_WeldingTo_GNo', 'WPSH_BM_WeldingTo_PNo', 'WPSH_BM_Backing_Backing', 'WPSH_BM_Backing_GNo',
  'WPSH_BM_Backing_PNo', 'WPSH_BM_Backing_Material', 'WPSH_BM_Retainer', 'WPSH_BM_Notes',
  'WPSH_TRQ_GroovewithoutImpact_Min', 'WPSH_TRQ_GroovewithoutImpact_Max', 'WPSH_TRQ_GroovewithImpact_Min',
  'WPSH_TRQ_GroovewithImpact_Max', 'WPSH_TRQ_FilletwithoutImpact_Min', 'WPSH_TRQ_FilletwithoutImpact_Max',
  'WPSH_TRQ_FilletwithImpact_Min', 'WPSH_TRQ_FilletwithImpact_Max', 'WPSH_FM_Add_Del_Filler',
  'WPSH_FM_Add_Del_Flux', 'WPSH_FM_AlloyElement', 'WPSH_FM_AlloyFlux', 'WPSH_FM_FilerMetal',
  'WPSH_FM_FluxFromRecrush', 'WPSH_FM_FluxItemBatch', 'WPSH_FM_FluxItemCode', 'WPSH_FM_FluxType',
  'WPSH_consumRadio', 'WPSH_filterRadio', 'WPSH_fluxRadio', 'wld_Joints_Type', 'wld_TRQ_Type'
];

const PAGE1_OVERLAY_FIELDS = [
  'WPSH_Project_No', 'WPSH_WPS_No', 'WPSH_WPS_Revi_No', 'WPSH_WPS_Date', 'WPS_QualifiedTo',
  'WPSH_CompanyName', 'WPSH_SupportingPQR', 'WPSH_RefDoc', 'WPSH_Scope', 'WPSH_JointDesc',
  'WPSH_BM_Type_Type', 'WPSH_BM_Type_GNo', 'WPSH_BM_Type_PNo', 'WPSH_BM_WeldingTo_Type',
  'WPSH_BM_WeldingTo_GNo', 'WPSH_BM_WeldingTo_PNo', 'WPSH_BM_Backing_Material', 'WPSH_BM_Notes',
  'WPSH_TRQ_BaseMetalCRO_Min', 'WPSH_TRQ_BaseMetalCRO_Max', 'WPSH_TRQ_BaseMetalHRO_Min',
  'WPSH_TRQ_BaseMetalHRO_Max', 'WPSH_FM_Add_Del_orchange', 'WPSH_FM_NominalFlux', 'WPSH_FM_AlloyElement',
  'WPSH_FM_FilerMetal', 'WPSH_FM_FluxFromRecrush', 'WPSH_FM_FluxItemBatch', 'WPSH_FM_FluxItemCode',
  'WPSH_FM_FluxType', 'WPSH_consumRadio', 'WPSH_filterRadio', 'WPSH_fluxRadio',
  'wld_Joints_Type', 'wld_TRQ_Type'
];

const PAGE1_TUBESHEET_FIELDS = [
  'WPSH_Project_No', 'WPSH_WPS_No', 'WPSH_WPS_Revi_No', 'WPSH_WPS_Date', 'WPS_QualifiedTo',
  'WPSH_CompanyName', 'WPSH_SupportingPQR', 'WPSH_RefDoc', 
  'WPSH_BM_BaseMetal1_ProductForm', 'WPSH_BM_BaseMetal1_Spec', 'WPSH_BM_BaseMetal1_PNo',
  'WPSH_BM_BaseMetal1_GNo', 'WPSH_BM_BaseMetal1_Thick', 'WPSH_BM_BaseMetal1_Diam',
  'WPSH_BM_WeldBaseMetal2_ProductForm', 'WPSH_BM_WeldBaseMetal2_Spec', 'WPSH_BM_WeldBaseMetal2_PNo',
  'WPSH_BM_WeldBaseMetal2_GNo', 'WPSH_BM_WeldBaseMetal2_Thick', 'WPSH_BM_WeldBaseMetal2_Diam',
  'WPSH_JD_JointType', 'WPSH_JD_HoleId', 'WPSH_JD_TubePitch', 'WPSH_JD_GrooveDepth',
  'WPSH_JD_TubeProj', 'WPSH_JD_WidthLigament', 'WPSH_JD_Pattern', 'WPSH_JD_GrooveAngle',
  'WPSH_JD_GrooveType', 'WPSH_FM_PreFillerMetal', 'WPSH_FM_WithFillerMetal', 'WPSH_FM_FillerProduct',
  'WPSH_FM_Inserts', 'WPSH_FM_FillerFlux'
];

const PAGE2_FIELDS = [
  'WPSD_WP_Maximumtravelspeed_DataLogger', 'WPSD_WP_MaximumInterpassTemp_DataLogger', 'WPSD_WP_MinimumPreheatTemp_DataLogger'
];

// Common fields for both Groove and Overlay
const PAGE3_COMMON_FIELDS = [
  'WPSH_PWHeatTre_Heating_Method', 'WPSH_PWHeatTre_Cooling_Method',
  'WPSH_Tech_Inital_InterpassCleaning', 'WPSH_Tech_Peening', 'WPSH_Tech_SupplementaryMFControl',
  'WPSH_Tech_SurfacePreparation', 'WPSH_Tech_Oscillation',
  'WPSH_PreparedBy_Name', 'WPSH_PreparedBy_Date', 'WPSH_ApprovedBy_Name', 'WPSH_ApprovedBy_Date'
];

// Groove-specific additional fields
const PAGE3_GROOVE_SPECIFIC = [
  'WPSH_Tech_BackGougingMethod', 'WPSH_Tech_GroovePreparation', 
  'WPSH_Tech_UseOfThermalProcess', 'WPSH_Tech_ClosedtooutChamber'
];

// Overlay-specific additional fields  
const PAGE3_OVERLAY_SPECIFIC = [
  'WPSH_Tech_Weld_deposit_chemistry', 'WPSH_Tech_Oscillation_Width',
  'WPSH_Tech_Oscillation_Freq', 'WPSH_Tech_Filler_Metal_Del'
];

const PAGE3_GROOVE_FIELDS = [...PAGE3_COMMON_FIELDS, ...PAGE3_GROOVE_SPECIFIC];
const PAGE3_OVERLAY_FIELDS = [...PAGE3_COMMON_FIELDS, ...PAGE3_OVERLAY_SPECIFIC];

const TUBESHEET_PAGE2_FIELDS = [
  'WPSH_Tech_Expansion', 'WPSH_Tech_StringWave', 'WPSH_Tech_MethodofCleaning', 'WPSH_Tech_OrifceCupSize',
  'WPSH_Tech_SingleMultiElectrode', 'WPSH_Tech_Oscillation', 'WPSH_Tech_Peening', 'WPSH_Tech_ClosedtooutChamber',
  'WPSH_Tech_UseOfThermalProcess', 'WPSH_PWHeatTre_Temperature', 'WPSH_PWHeatTre_Time', 'WPSH_PWHeatTre_Type',
  'WPSH_PWHeatTre_Wrap_insulation_material', 'WPSH_PWHeatTre_Heating_Rate', 'WPSH_PWHeatTre_Cooling_Rate',
  'WPSH_PWHeatTre_LoadingTemp', 'WPSH_PWHeatTre_UnloadingTemp', 'WPSH_PWHeatTre_PWHTNotes', 'WPSH_Notes'
];

// Helper function to extract fields from header
const extractFields = (header, fields) => {
  const result = {};
  fields.forEach(field => {
    if (header.hasOwnProperty(field)) {
      result[field] = header[field];
    } else {
      result[field] = "";
    }
  });
  return result;
};

// ============================================================================
// GROOVE WPS CONFIGURATION
// ============================================================================

// USED - Used in WPS groove transaction pages for field validation and processing
export let WPS_Groove_Detail = [
  // Single value fields (Type: "S")
  { Column: "WPSD_WP_MinimumPreheatTemp", Type: "S" },
  { Column: "WPSD_WP_MaximumInterpassTemp", Type: "S" },
  { Column: "WPSD_WP_TungstenSIze", Type: "S" },
  { Column: "WPSD_WP_TungstenType", Type: "S" },
  { Column: "WPSD_WP_FillerMetalProductForm", Type: "S" },
  { Column: "WPSD_WP_ShieldingFlowRate", Type: "S" },
  { Column: "WPSD_WP_ShieldingGasType", Type: "S" },
  { Column: "WPSD_WP_BackingFlowRate", Type: "S" },
  { Column: "WPSD_WP_BackingGasType", Type: "S" },
  { Column: "WPSD_WP_TrailingFlowRate", Type: "S" },
  { Column: "WPSD_WP_TrailingGasType", Type: "S" },
  { Column: "WPSD_WP_ArcTransferMode", Type: "S" },
  { Column: "WPSD_WP_StringOrWeave", Type: "S" },
  { Column: "WPSD_WP_Orifice_GasCupSize", Type: "S" },
  { Column: "WPSD_WP_CTWD", Type: "S" },
  { Column: "WPSD_WP_Pulsing_current", Type: "S" },
  { Column: "WPSD_WP_Multiple_SinglePassPerSide", Type: "S" },
  { Column: "WPSD_WP_Multiple_SingleLayer", Type: "S" },
  { Column: "WPSD_WP_Multi_SingleElectrode", Type: "S" },
  { Column: "WPSD_WP_ElectrodeSpacing_Late", Type: "S" },
  { Column: "WPSD_WP_ElectrodeSpacing_Logit", Type: "S" },
  { Column: "WPSD_WP_ElectrodeAngleDegree", Type: "S" },
  { Column: "WPSD_WP_Max_Pass_Thickness", Type: "S" },

  // Array fields (Type: "A")
  { Column: "WPSD_WP_MinimumBeadLength", Type: "A" },
  { Column: "WPSD_WP_WeldConsumable", Type: "A" },
  { Column: "WPSD_WP_FillerMetalSIze", Type: "A" },
  { Column: "WPSD_WP_BatchNumber", Type: "A" },
  { Column: "WPSD_WP_LayerNumber", Type: "A" },
  { Column: "WPSD_WP_PositionOfGroove", Type: "A" },
  { Column: "WPSD_WP_WeldProgression", Type: "A" },
  { Column: "WPSD_WP_CurrentPolarity", Type: "A" },
  { Column: "WPSD_WP_Volts", Type: "A" },
  { Column: "WPSD_WP_TravelSpeed", Type: "A" },
  { Column: "WPSD_WP_MaximumHeatInput", Type: "A" },
  { Column: "WPSD_WP_Amperes", Type: "A" }
];



// ============================================================================
// OVERLAY WPS CONFIGURATION
// ============================================================================



// USED - Used in WPS overlay transaction pages for field processing
export let WPS_Overlay_Detail = [
  { Column: "WPSD_WP_MinimumPreheatTemp", Type: "S" },
  { Column: "WPSD_WP_MaximumInterpassTemp", Type: "S" },
  { Column: "WPSD_WP_FillerMetalProductForm", Type: "S" },
  { Column: "WPSD_WP_FillerMetalSIze", Type: "A" },
  { Column: "WPSD_WP_WeldConsumable", Type: "A" },
  { Column: "WPSD_WP_BatchNumber", Type: "A" },
  { Column: "WPSD_WP_PositionOfOverlay", Type: "A" },
  { Column: "WPSD_WP_WeldProgression", Type: "A" },
  { Column: "WPSD_WP_CurrentPolarity", Type: "A" },
  { Column: "WPSD_WP_Amperes", Type: "A" },
  // { Column: "WPSD_WP_Amperes1", Type: "A" }, // Commented out - second amperage field not needed
  { Column: "WPSD_WP_Volts", Type: "A" },
  { Column: "WPSD_WP_TravelSpeed", Type: "A" },
  { Column: "WPSD_WP_MinimumBeadLength", Type: "A" },
  { Column: "WPSD_WP_MaximumHeatInput", Type: "A" },
  { Column: "WPSD_WP_TungstenSIze", Type: "S" },
  { Column: "WPSD_WP_TungstenType", Type: "S" },
  { Column: "WPSD_WP_ShieldingFlowRate", Type: "S" },
  { Column: "WPSD_WP_ShieldingGasType", Type: "S" },
  { Column: "WPSD_WP_TrailingFlowRate", Type: "S" },
  { Column: "WPSD_WP_TrailingGasType", Type: "S" },
  { Column: "WPSD_WP_ArcTransferMode", Type: "S" },
  { Column: "WPSD_WP_StringOrWeave", Type: "S" },
  { Column: "WPSD_WP_Orifice_GasCupSize", Type: "S" },
  { Column: "WPSD_WP_CTWD", Type: "S" },
  { Column: "WPSD_WP_Multiple_SinglePassPerSide", Type: "S" },
  { Column: "WPSD_WP_Multiple_SingleLayer", Type: "S" },
  { Column: "WPSD_WP_Multi_SingleElectrode", Type: "S" },
  { Column: "WPSD_WP_ElectrodeSpacing_Late", Type: "S" },
  { Column: "WPSD_WP_ElectrodeSpacing_Logit", Type: "S" },
  { Column: "WPSD_WP_ElectrodeAngleDegree", Type: "S" },
  { Column: "WPSD_WP_Weld_Bead_Overlap", Type: "S" },


];

// USED - Imported in GrooveTransaction.jsx and WPSTubesheetTransaction.jsx for header initialization
export const WPSHeader = (wpstype) => {
  return {
    WPSH_Status: Status.StatusTempSaved,
    WPSH_Approval_Status: Status.StatusTempSaved,
    WPSH_Project_No: "",
    WPSH_WPS_No: "",
    WPSH_WPS_Revi_No: "",
    WPSH_WPS_Date: todayDate,
    WPS_QualifiedTo: "",
    WPSH_CompanyName: "Godrej & Boyce Mfg. Co. Ltd.",
    WPSH_SupportingPQR: "",
    WPSH_RefDoc: "",
    WPSH_Scope: "",
    WPSH_JointDesc: "",
    WPSH_BM_Type_GNo: "",
    WPSH_BM_Type_PNo: "",
    WPSH_BM_Type_Type: "",
    WPSH_MinOvlFinThick: "",
    WPSH_BM_WeldingTo_GNo: "",
    WPSH_BM_WeldingTo_PNo: "",
    WPSH_BM_WeldingTo_Type: "",
    WPSH_BM_Backing_Backing: "",
    WPSH_BM_Type_HT_Con: "",
    WPSH_BM_WeldingTo_HT_Con: "",
    WPSH_BM_Backing_GNo: "",
    WPSH_BM_Backing_PNo: "",
    WPSH_BM_Notes: "",
    WPSH_BM_Retainer: "",
    WPSH_BM_Backing_Material: "",
    WPSH_TRQ_GroovewithoutImpact_Max: "",
    WPSH_TRQ_GroovewithoutImpact_Min: "",
    WPSH_TRQ_GroovewithImpact_Max: "",
    WPSH_TRQ_GroovewithImpact_Min: "",
    WPSH_TRQ_FilletwithoutImpact_Max: "",
    WPSH_TRQ_FilletwithoutImpact_Min: "",
    WPSH_TRQ_FilletwithImpact_Max: "",
    WPSH_TRQ_FilletwithImpact_Min: "",
    WPSH_TRQ_BaseMetalCRO_Min: "",
    WPSH_TRQ_BaseMetalCRO_Max: "",
    WPSH_TRQ_BaseMetalHRO_Max: "",
    WPSH_TRQ_BaseMetalHRO_Min: "",
    WPSH_FM_FluxItemCode: "",
    WPSH_FM_FluxItemBatch: "",
    WPSH_FM_FilerMetal: "",
    WPSH_FM_FluxFromRecrush: "",
    WPSH_FM_FluxType: "",

    WPSH_FM_AlloyFlux: "",
    WPSH_FM_AlloyElement: "",
    WPSH_FM_Add_Del_Filler: "",
    WPSH_FM_Add_Del_Flux: "",
    WPSH_FM_Add_Del_orchange: "",
    WPSH_FM_NominalFlux: "",
    // wld_pdf_image: [],

    // wld_pdf_image: [],

    wld_pdf_image: [
      { Image1: [""] },
      { Image2: [""] },
      { Image3: [""] },
      { Image1Data: [{ lastModified: "", lastModifiedDate: "", name: "", size: "", type: "", webkitRelativePath: "" }] },
      { Image2Data: [{ lastModified: "", lastModifiedDate: "", name: "", size: "", type: "", webkitRelativePath: "" }] },
      { Image3Data: [{ lastModified: "", lastModifiedDate: "", name: "", size: "", type: "", webkitRelativePath: "" }] },



    ],

    Pdf_images: [
      { tube_Hole_ligament: [""] },
      { tube_Hole: [""] },
      { tube_Weld_Joint: [""] },
    ],

    WPSH_PHT_Root_spacing: "",
    // WPSH_PHT_ApplicationStd: "",
    // WPSH_PHT_Desc: "",
    WPSH_PWHeatTre_Temperature: "",
    WPSH_PWHeatTre_Time: "",
    WPSH_PWHeatTre_Heating_Rate: "",
    WPSH_PWHeatTre_Heating_Method: "",
    WPSH_PWHeatTre_Cooling_Rate: "",
    WPSH_PWHeatTre_Cooling_Method: "",
    WPSH_PWHeatTre_LoadingTemp: "",
    WPSH_PWHeatTre_UnloadingTemp: "",
    WPSH_PWHeatTre_PWHTNotes: "",
    WPSH_PWHeatTre_Type: "NA",
    WPSH_PWHeatTre_Wrap_insulation_material: "",
    WPSH_PWHeatTre_Notes: "",
    WPSH_PWHeatTre_Post_Heat_temp: "",
    WPSH_PWHeatTre_Post_Heat_time: "",
    // WPSH_PWHeatTre_Wrap_insulation_material: "",
    WPSH_PWHeatTre_Weld_Time: "",
    WPSH_PWHeatTre_Weld_Notes: "",
    WPSH_PWHeatTre_ISR_Temp: "",
    WPSH_PWHeatTre_ISR_Time: "",
    WPSH_Tech_BackGougingMethod: "",
    WPSH_Tech_ClosedtooutChamber: "",
    WPSH_Tech_GroovePreparation: "",
    WPSH_Tech_Inital_InterpassCleaning: "",
    WPSH_Tech_Peening: "",
    WPSH_Tech_SupplementaryMFControl: "",
    WPSH_Tech_SurfacePreparation: "",
    WPSH_Tech_UseOfThermalProcess: "",
    WPSH_Tech_Weld_deposit_chemistry: "",
    // WPSH_Tech_Change_Electrode_dia:"",
    WPSH_Tech_Oscillation: "",
    WPSH_Notes: "",
    WPSH_PreparedBy_Date: todayDate,
    WPSH_PreparedBy_Name: "",
    WPSH_ApprovedBy_Date: todayDate,
    WPSH_ApprovedBy_Name: "",
    WPSH_ApprovedBy_Remark: "",
    WPSH_PreparedBy_Remark: "",
    WPSH_Prepared_By: sessionStorage.getItem("User"),
    WPSH_UpdatedBy: "",
    WPSH_UpdatedOn: "",
    WPSH_ApprovedBy: sessionStorage.getItem("UserName"),
    WPSH_consumRadio: "Required",
    WPSH_fluxRadio: "Required",
    WPSH_filterRadio: "Required",
    wld_TRQ_Type: "Welded",
    wld_Joints_Type: "Joint section",
    // wld_Approval_Type: Status.StatusPending,
    wld_WPSType: wpstype,
    WPSH_Tech_Oscillation_Width: "",
    WPSH_Tech_Oscillation_Freq: "",
    WPSH_Tech_Filler_Metal_Del: "",
    WPS_Change_No: "0",
    wps_signaturePrepared: "",
    wps_signatureApproved: "",
    SK: "",
    PK: wpstype,
    WPSD_WP_MinimumPreheatTemp_DataLogger: "",
    WPSD_WP_MaximumInterpassTemp_DataLogger: "",
    WPSD_WP_Maximumtravelspeed_DataLogger: "",
    WPSH_WPS_Process: "",
    WPSH_WPS_Type: "",
    WPSH_WPS_WeldLayer: "",
    //below for tubesheet
    WPSH_WPS_SFA: "",
    WPSH_BM_BaseMetal1_ProductForm: "",
    WPSH_BM_BaseMetal1_Spec: "",
    WPSH_BM_BaseMetal1_PNo: "",
    WPSH_BM_BaseMetal1_GNo: "",
    WPSH_BM_BaseMetal1_Thick: "",
    WPSH_BM_BaseMetal1_Diam: "",
    WPSH_BM_WeldBaseMetal2_ProductForm: "",
    WPSH_BM_WeldBaseMetal2_Spec: "",
    WPSH_BM_WeldBaseMetal2_PNo: "",
    WPSH_BM_WeldBaseMetal2_GNo: "",
    WPSH_BM_WeldBaseMetal2_Thick: "",
    WPSH_BM_WeldBaseMetal2_Diam: "",
    WPSH_JD_JointType: wpstype === "Tubesheet" ? "Tube to Tubesheet" : "",
    WPSH_JD_HoleId: "",
    WPSH_JD_TubePitch: "",
    WPSH_JD_GrooveDepth: "",
    WPSH_JD_TubeProj: "",
    WPSH_JD_WidthLigament: "",
    WPSH_JD_Pattern: "",
    WPSH_JD_GrooveAngle: "",
    WPSH_JD_GrooveType: "",
    //Tubesheet Filler Metal fields
    WPSH_FM_PreFillerMetal: "",
    WPSH_FM_WithFillerMetal: "",
    WPSH_FM_FillerProduct: "",
    WPSH_FM_Inserts: "",
    WPSH_FM_FillerFlux: "",
    //Tubesheet Other Variables fields
    WPSH_Tech_Expansion: "",
    WPSH_Tech_StringWave: "",
    WPSH_Tech_MethodofCleaning: "",
    WPSH_Tech_SingleMultiElectrode: "",
    WPSH_Tech_OrifceCupSize: "",
    WPSH_Tech_Others: "",
  };
};

// USED - Used in General.jsx for page 1 initialization
export const WPSPage1 = extractFields(WPSHeader("Groove"), PAGE1_GROOVE_FIELDS);

// USED - Used in General.jsx for overlay page initialization
export const WPSPage1_Overlay = extractFields(WPSHeader("Overlay"), PAGE1_OVERLAY_FIELDS);



// USED - Used for page 3 initialization in WPS forms
export const WPSPage3_Groove = extractFields(WPSHeader("Groove"), PAGE3_GROOVE_FIELDS);
export const WPSPage3_Overlay = extractFields(WPSHeader("Overlay"), PAGE3_OVERLAY_FIELDS);
export const WPSPage3 = WPSPage3_Groove; // Backward compatibility

// USED - Used for page 2 initialization in WPS forms
export const WPSPage2 = extractFields(WPSHeader("Groove"), PAGE2_FIELDS);





// ============================================================================
// TUBESHEET WPS CONFIGURATION
// ============================================================================



// USED - Used in WPSTubesheetGeneral.jsx and WPSTubesheetProcedureSpec.jsx for tubesheet page 1 initialization
export const Tubesheet_WPSPage1 = extractFields(WPSHeader("Tubesheet"), PAGE1_TUBESHEET_FIELDS);

// USED - Used in WPSTubesheetProcedureSpec.jsx for tubesheet page 2 initialization
export const Tubesheet_WPSPage2 = extractFields(WPSHeader("Tubesheet"), TUBESHEET_PAGE2_FIELDS);

// USED - Used in WPSTubesheetProcedureSpec.jsx for tubesheet detail field processing
export let WPS_Tubesheet_Detail = [
  // Single value fields (Type: "S")
  { Column: "WPSD_WP_Type", Type: "S" },
  { Column: "WPSD_WP_MinimumPreheatTemp", Type: "S" },
  { Column: "WPSD_WP_MaximumInterpassTemp", Type: "S" },
  { Column: "WPSD_WP_PostHeating", Type: "S" },
  { Column: "WPSD_WP_TungstenType", Type: "S" },
  { Column: "WPSD_WP_WeldPosition", Type: "S" },
  { Column: "WPSD_WP_WeldProgression", Type: "S" },
  { Column: "WPSD_WP_NoofPasses", Type: "S" },
  { Column: "WPSD_WP_ArcVoltageControl", Type: "S" },
  { Column: "WPSD_WP_ShieldingGasType", Type: "S" },
  { Column: "WPSD_WP_ShieldingFlowRate", Type: "S" },
  { Column: "WPSD_WP_BackingGasType", Type: "S" },
  { Column: "WPSD_WP_BackingFlowRate", Type: "S" },
  { Column: "WPSD_WP_AuxiGasShieldSystem", Type: "S" },

  // Array fields (Type: "A")
  { Column: "WPSD_WP_LayerNumber", Type: "A" },
  { Column: "WPSD_WP_FillerMetalSIze", Type: "A" },
  { Column: "WPSD_WP_ItemCode", Type: "A" },
  { Column: "WPSD_WP_BatchNumber", Type: "A" },
  { Column: "WPSD_WP_Amperes", Type: "A" },
  { Column: "WPSD_WP_Pulsing_current", Type: "A" },
  { Column: "WPSD_WP_Pulsing_current_others", Type: "A" },
  { Column: "WPSD_WP_Volts", Type: "A" },
  { Column: "WPSD_WP_TravelSpeed", Type: "A" },
  { Column: "WPSD_WP_HeatInput", Type: "A" },
  { Column: "WPSD_WP_CurrentPolarity", Type: "A" }
];




// ============================================================================
// VALIDATION CONFIGURATION
// ============================================================================



